import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  HeartHandshake,
  Loader2,
  LogOut,
  Mail,
  Phone,
  User2,
  Users,
} from 'lucide-react';
import type { User } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { gsgoInputClass, gsgoLabelClass, gsgoSectionEyebrowClass, gsgoTextareaClass } from '@/lib/gsgo-form-styles';
import { Avatar } from '@/components/ui/Avatar';
import {
  buildProfileFirestorePayload,
  emptyGsgoProfileForm,
  mapFirestoreProfileToForm,
  type GsgoProfileFormData,
} from '@/lib/user-profile';

interface UserProfileSummary {
  displayName: string;
  photoUrl?: string | null;
}

interface CustomerProfilePageProps {
  readonly userId: string;
  readonly user: User;
  readonly userProfile: UserProfileSummary | null;
  readonly isAdmin: boolean;
  readonly defaultDisplayName?: string;
  readonly defaultEmail?: string | null;
  readonly onSignOut: () => Promise<void>;
  showNotify: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const relationshipOptions = [
  'Patient',
  'Caregiver',
  'Family member',
  'Community supporter',
  'Volunteer',
  'Partner organization',
  'Prefer not to say',
];

function getAuthProviderLabel(user: User): string {
  const isGoogleUser = user.providerData.some((provider) => provider.providerId === 'google.com');
  if (isGoogleUser) return 'Signed in with Google';
  if (user.email) return 'Signed in with Email';
  return 'Verified account';
}

export function CustomerProfilePage({
  userId,
  user,
  userProfile,
  isAdmin,
  defaultDisplayName = '',
  defaultEmail = '',
  onSignOut,
  showNotify,
}: CustomerProfilePageProps): JSX.Element {
  const [form, setForm] = useState<GsgoProfileFormData>(emptyGsgoProfileForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const displayName = userProfile?.displayName || user.displayName || defaultDisplayName || 'Guest User';
  const photoUrl = user.photoURL || userProfile?.photoUrl || undefined;
  const authProviderLabel = getAuthProviderLabel(user);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);

    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'main');

    getDoc(profileRef)
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? (snap.data() as Record<string, unknown>) : undefined;
        setForm(
          mapFirestoreProfileToForm(data, {
            displayName: defaultDisplayName,
            contactEmail: defaultEmail ?? '',
          })
        );
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[CustomerProfilePage] Failed to load profile:', err);
          setForm(
            mapFirestoreProfileToForm(undefined, {
              displayName: defaultDisplayName,
              contactEmail: defaultEmail ?? '',
            })
          );
          showNotify('Could not load profile. You can still save.', 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, defaultDisplayName, defaultEmail, showNotify]);

  const handleChange =
    (field: keyof GsgoProfileFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedDisplayName = form.displayName.trim();
    if (!trimmedDisplayName) {
      showNotify('Display name is required.', 'error');
      return;
    }

    setSaving(true);
    try {
      const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'main');
      const existing = await getDoc(profileRef);
      const existingPhotoUrl = existing.exists()
        ? (existing.data() as Record<string, unknown>).photoUrl
        : null;
      const payload = buildProfileFirestorePayload(
        {
          ...form,
          displayName: trimmedDisplayName,
        },
        existingPhotoUrl
      );
      await setDoc(profileRef, payload, { merge: true });
      showNotify('Profile updated successfully.', 'success');
    } catch (err) {
      console.error('[CustomerProfilePage] Failed to save profile:', err);
      showNotify('Failed to save profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await onSignOut();
    } catch (err) {
      console.error('[CustomerProfilePage] Sign-out failed:', err);
      showNotify('Failed to sign out. Please try again.', 'error');
    } finally {
      setSigningOut(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center" role="main" aria-busy="true">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={40} className="animate-spin text-smb-primary" aria-hidden="true" />
          <p className="text-sm text-smb-slate-600">Loading profile…</p>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="bg-gray-50 min-h-screen" role="main" aria-label="Edit profile">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm font-medium text-smb-primary-mid hover:text-smb-primary transition-colors mb-6"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Back to dashboard
        </Link>

        <section className="rounded-2xl overflow-hidden shadow-md border border-smb-primary-mid/30 p-6 sm:p-8 bg-white">
          <p className="text-sm font-semibold tracking-wide uppercase text-smb-primary-mid">Your account</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-smb-ink">Edit profile</h1>
          <p className="mt-3 max-w-3xl text-sm sm:text-base text-smb-slate-600">
            Manage your sign-in details, community preferences, and how GSGO can reach you.
          </p>
        </section>

        <section
          className="mt-6 rounded-2xl border border-smb-slate-100 bg-white shadow-sm p-6 sm:p-8"
          aria-labelledby="account-summary-heading"
        >
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:text-left sm:gap-6">
            <Avatar name={displayName} size="xl" url={photoUrl} className="mb-4 sm:mb-0 shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 id="account-summary-heading" className="text-xl font-bold text-smb-ink truncate">
                {displayName}
              </h2>
              {user.email && (
                <p className="text-sm text-smb-slate-600 mt-1 truncate">{user.email}</p>
              )}
              <p className="text-sm text-smb-slate-500 mt-1">{authProviderLabel}</p>
              {isAdmin && (
                <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded mt-2 font-bold">
                  ADMIN ACCESS
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              className="mt-4 sm:mt-0 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-red-50 text-red-600 font-semibold px-5 py-2.5 text-sm hover:bg-red-100 transition-colors disabled:opacity-70 disabled:cursor-not-allowed shrink-0"
            >
              <LogOut size={18} aria-hidden="true" />
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </section>

        <div className="mt-6 rounded-2xl border border-smb-slate-100 bg-white shadow-sm overflow-hidden">
          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            <p className={gsgoSectionEyebrowClass}>Community profile</p>
            <p className="mt-1 mb-6 text-sm text-smb-slate-600">
              Help us personalize your experience and connect you with CKD support, events, and ways to give.
            </p>

            <div className="space-y-8">
              <section aria-labelledby="your-account-heading">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-smb-primary-light/20 text-smb-primary flex items-center justify-center shrink-0">
                    <User2 size={20} aria-hidden="true" />
                  </div>
                  <div>
                    <p className={gsgoSectionEyebrowClass}>Your account</p>
                    <h2 id="your-account-heading" className="text-lg font-semibold text-smb-ink">
                      Basic details
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="profile-displayName" className={gsgoLabelClass}>
                      Display name *
                    </label>
                    <div className="relative">
                      <User2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smb-slate-500" aria-hidden="true" />
                      <input
                        id="profile-displayName"
                        type="text"
                        required
                        value={form.displayName}
                        onChange={handleChange('displayName')}
                        className={`${gsgoInputClass} pl-10`}
                        placeholder="Your name"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="profile-contactEmail" className={gsgoLabelClass}>
                      Email for GSGO updates
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smb-slate-500" aria-hidden="true" />
                      <input
                        id="profile-contactEmail"
                        type="email"
                        value={form.contactEmail}
                        onChange={handleChange('contactEmail')}
                        className={`${gsgoInputClass} pl-10`}
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-smb-slate-100" />

              <section aria-labelledby="stay-connected-heading">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-smb-primary-light/20 text-smb-primary flex items-center justify-center shrink-0">
                    <Users size={20} aria-hidden="true" />
                  </div>
                  <div>
                    <p className={gsgoSectionEyebrowClass}>Stay connected</p>
                    <h2 id="stay-connected-heading" className="text-lg font-semibold text-smb-ink">
                      How we can reach you
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="profile-contactPhone" className={gsgoLabelClass}>
                      Contact phone
                    </label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smb-slate-500" aria-hidden="true" />
                      <input
                        id="profile-contactPhone"
                        type="tel"
                        value={form.contactPhone}
                        onChange={handleChange('contactPhone')}
                        className={`${gsgoInputClass} pl-10`}
                        placeholder="+63 900 000 0000"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="profile-relationshipToCkd" className={gsgoLabelClass}>
                      Relationship to CKD
                    </label>
                    <select
                      id="profile-relationshipToCkd"
                      value={form.relationshipToCkd}
                      onChange={handleChange('relationshipToCkd')}
                      className={gsgoInputClass}
                    >
                      <option value="">Select one</option>
                      {relationshipOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              <hr className="border-smb-slate-100" />

              <section aria-labelledby="connection-heading">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-smb-primary-light/20 text-smb-primary flex items-center justify-center shrink-0">
                    <HeartHandshake size={20} aria-hidden="true" />
                  </div>
                  <div>
                    <p className={gsgoSectionEyebrowClass}>Your connection to GSGO</p>
                    <h2 id="connection-heading" className="text-lg font-semibold text-smb-ink">
                      Support interests and notes
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5">
                  <div>
                    <label htmlFor="profile-supportInterests" className={gsgoLabelClass}>
                      How you&apos;d like to stay involved
                    </label>
                    <div className="relative">
                      <HeartHandshake size={18} className="absolute left-3 top-3 text-smb-slate-500" aria-hidden="true" />
                      <textarea
                        id="profile-supportInterests"
                        rows={4}
                        value={form.supportInterests}
                        onChange={handleChange('supportInterests')}
                        className={`${gsgoTextareaClass} pl-10`}
                        placeholder="Share interests like volunteering, advocacy, events, or donor updates."
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="profile-profileNotes" className={gsgoLabelClass}>
                      Additional notes
                    </label>
                    <div className="relative">
                      <FileText size={18} className="absolute left-3 top-3 text-smb-slate-500" aria-hidden="true" />
                      <textarea
                        id="profile-profileNotes"
                        rows={4}
                        value={form.profileNotes}
                        onChange={handleChange('profileNotes')}
                        className={`${gsgoTextareaClass} pl-10`}
                        placeholder="Anything else you want the GSGO team to know."
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-10 pt-6 border-t border-smb-slate-100 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-smb-primary hover:bg-smb-primary-mid px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  'Save profile'
                )}
              </button>
              <Link
                to="/dashboard"
                className="rounded-xl border-2 border-smb-slate-100 px-5 py-2.5 text-sm font-semibold text-smb-slate-700 hover:bg-smb-slate-50/50 transition-colors"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
